import { type FontItem, type FontMaterialRaw } from '@meitu/xdesign-widgets';
import type { IFont } from '@/version-types';

const parseFontMetrics = (raw: FontMaterialRaw) => {
    if (!raw['font_metrics']) return undefined;

    try {
        const parsed = JSON.parse(raw.font_metrics);
        const metrics = parsed?.metrics;

        if (!metrics) return undefined;
        const { ascender, descender, unitsPerEm } = metrics;

        if ([ascender, descender, unitsPerEm].some((value) => typeof value !== 'number')) return undefined;

        return {
            ascender,
            descender,
            unitsPerEm,
        };
    } catch (_) {
        return undefined;
    }
};

/**
 * 维护业务字体目录。
 *
 * 只负责字体列表与查询，不持有 subset/fallback 运行时状态。
 *
 * FIXME:
 *
 * 原本编辑器的字体库只有 IFont 类型，并没有 FontItem 类型（FontItem 类型是字体列表组件所需要的类型）
 * 无论是 FontMaterialRaw 还是 FontItem，都属于外部的业务数据结构，而不是编辑器内部的数据结构。
 * 并且原本也没有 setRaw 方法，但反而现在 setRaw 变成外部给编辑器设置字体素材的入口，这是不合理的。
 * 不同字体的数据结构是必然复杂度，需要去解决，但不应将这层复杂度引入编辑器内部。
 * FontLibrary 应该只有 setFonts/getFonts 方法，而不应该有 setRaw/toFonts/toFontItems 方法。
 */
export class FontLibrary {
    private raw: FontMaterialRaw[] = [];
    private fonts: IFont[] = [];
    private fontMapByFamily: Record<string, IFont> = {};
    private fontMapById: Record<string, IFont> = {};

    private fontItems: FontItem[] = [];
    private fontItemsMapById: Record<string, FontItem> = {};

    public setRaw(raw: FontMaterialRaw[]) {
        this.raw = raw;
        this.fonts = this.toFonts(raw);
        this.fontItems = this.toFontItems(raw);

        this.fontMapByFamily = {};
        this.fontMapById = {};
        this.fontItemsMapById = {};

        this.fonts.forEach((font) => {
            this.fontMapByFamily[font.name] = font;
            this.fontMapById[font.id] = font;
        });

        this.fontItems.forEach((item) => {
            this.fontItemsMapById[String(item.id)] = item;
        });
    }

    public toFonts(raw: FontMaterialRaw[]) {
        return raw.map<IFont>((item) => {
            const metrics = parseFontMetrics(item);
            return {
                id: String(item.id),
                name: item.font_name,
                ttf: item.font_file?.url || '',
                woff2: item.woff2_file?.url,
                zip: item.zip_file?.url,
                ...(metrics ? { metrics } : {}),
            };
        });
    }

    public toFontItems(raw: FontMaterialRaw[]) {
        return raw.map<FontItem>((item) => ({
            category_id: item.category_id,
            category_name: item.category_name || 'Font',
            id: item.id,
            font_name: item.font_name,
            is_vip: String(item.threshold),
            material_id: item.id,
            name: item.default_name,
            thumbnail: item.thumbnail?.url,
            thumbnail_text_dark: item.thumbnail_text_dark?.url,
            thumbnail_text_white: item.thumbnail_text_white?.url,
            zip_file: item.zip_file?.url,
            // 原先定义的名字，后续统一用font_file就可以
            zip_url: item.woff2_file?.url || item.font_file.url,
            font_file: item.woff2_file?.url || item.font_file.url,
            corner_mark: item.corner_mark?.url,
            threshold: item.threshold,
            type_id: item.type_id,
            woff2_file_url: item.woff2_file?.url,
            font_group_name: item.font_group_name,
            font_weight_value: item.font_weight_value,
            font_group_thumbnail_dark: item.font_group_thumbnail_dark?.url || '',
            font_group_thumbnail_white: item.font_group_thumbnail_white?.url || '',
        }));
    }

    public getRaw() {
        return this.raw;
    }

    public getFonts() {
        return this.fonts;
    }

    public getByFamily(fontFamily: string) {
        return this.fontMapByFamily[fontFamily];
    }

    public getById(fontId: string) {
        return this.fontMapById[fontId];
    }

    /**
     * 按格式优先级返回首个可用字体源，并保留实际命中的资源格式。
     *
     * // FIXME: 命名不准确
     *
     * @param fontFamily - 字体家族名称
     * @param formats - 格式优先级数组
     * @returns 首个可用字体源，并保留实际命中的资源格式。
     */
    public resolveSource(fontFamily: string, formats: ReadonlyArray<'ttf' | 'woff2'>) {
        const font = this.getByFamily(fontFamily);
        if (!font) return null;

        for (const format of formats) {
            const url = font[format];
            if (url) return { url, format };
        }

        return null;
    }

    public getFontItemById(fontId: string) {
        return this.fontItemsMapById[fontId];
    }

    public getFontItems() {
        return this.fontItems;
    }
}
